import React, { useMemo, useRef, useState } from 'react';
import {
  ComboBox,
  Dialog,
  DialogFooter,
  DialogType,
  Dropdown,
  IconButton,
  IComboBoxOption,
  Label,
  Overlay,
  Panel,
  PanelType,
  Spinner,
  SpinnerSize,
  Stack,
  TextField,
  Toggle,
} from '@fluentui/react';
import { initializeIcons } from '@fluentui/font-icons-mdl2';
import { IHeaderMenuProps } from './interfaces/IHeaderMenu';

type MenuType = 'link' | 'cat';

interface IApiMenuItem {
  type: MenuType;
  text?: string;
  target?: string;
  href?: string;
  showOnlyLoggedIn?: boolean;
  childnotes?: boolean;
  tree?: {
    id?: number;
    Id?: number;
    cat_slug?: string;
    Slug?: string;
  };
}

interface ICategoryNode {
  Id: number;
  Title: string;
  Slug: string;
  nodes?: ICategoryNodeItem[];
  children?: ICategoryNode[];
}

interface ICategoryNodeItem {
  Id: number;
  Title: string;
  Slug: string;
}

interface IEditableMenuItem {
  id: string;
  type: MenuType;
  text: string;
  adress: string;
  target: '_self' | '_blank';
  showOnlyLoggedIn: boolean;
  slug: string;
  categoryId: number | null;
  childnotes: boolean;
  levels: number;
}

type DropPosition = 'before' | 'after';

const DND_GAP_PX = 22;
const DND_LINE_THICKNESS_PX = 2;
const DND_LINE_OFFSET_PX = 12;

const API_URL = `${window.location.origin}/api/content.php`;
const UI_FONT = '"Marmelad", sans-serif';
const UI_PANEL_BG = '#000 url(/styles/images/webp/content_bg.webp) center top / cover no-repeat';
const UI_COLORS = {
  bg: '#000',
  panel: '#000',
  card: 'rgba(0,0,0,.4)',
  border: '#ffd36b',
  borderStrong: '#c77e19',
  text: '#fdf2b4',
  accent: '#f7b10a',
  accentHover: '#ffd36b',
  accentBgHover: '#b06601',
  dangerBgHover: '#9f1600',
};

initializeIcons();

function uid(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function mapApiItemToEditable(item: IApiMenuItem): IEditableMenuItem {
  const type = item.type === 'cat' ? 'cat' : 'link';
  return {
    id: uid(),
    type,
    text: item.text ?? '',
    adress: item.href ?? '',
    target: item.target === '_blank' ? '_blank' : '_self',
    showOnlyLoggedIn: Boolean(item.showOnlyLoggedIn),
    slug: item.tree?.cat_slug ?? item.tree?.Slug ?? '',
    categoryId: item.tree?.id ?? item.tree?.Id ?? null,
    childnotes: Boolean(item.childnotes),
    levels: 3,
  };
}

function decodeHtml(value: string): string {
  if (!value || typeof window === 'undefined') return value;
  if (!/&[a-z#][a-z0-9]+;/i.test(value)) return value;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

function flattenCategories(categories: ICategoryNode[]): ICategoryNode[] {
  const all: ICategoryNode[] = [];
  const walk = (nodes: ICategoryNode[]) => {
    nodes.forEach((n) => {
      all.push(n);
      if (Array.isArray(n.children) && n.children.length > 0) {
        walk(n.children);
      }
    });
  };
  walk(categories);
  return all;
}

function buildPreviewHtml(items: IEditableMenuItem[], categoryMap: Map<number, ICategoryNode>): string {
  const esc = (v: string) =>
    v
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const renderNodes = (nodes: ICategoryNodeItem[] = []) => {
    if (!nodes.length) return '';
    const lis = nodes
      .map(
        (n) =>
          `<li class=' fstlvl-nodes'><a class='icon' href='/${esc(n.Slug)}'><svg height='13' width='10'><use xlink:href='#triangle' style='fill:#f99a00;stroke:#f99a00; '></use></svg> ${esc(n.Title)}</a></li>`,
      )
      .join('');
    return `<div class='node-list'><ul>${lis}</ul></div>`;
  };

  const renderChildren = (children: ICategoryNode[] = [], level: number, maxLevel: number): string => {
    if (!children.length || level > maxLevel) return '';
    return children
      .map((c) => {
        const childNodes = renderNodes(c.nodes ?? []);
        const deeper = renderChildren(c.children ?? [], level + 1, maxLevel);
        const wrapper = level === 2 ? 'sndlvl' : `rndlvl lvl-${level}`;
        const nodeWrap = `sndlvl-nodes${level >= 3 ? ` lvl-${level}-nodes` : ''}`;
        const content = childNodes || deeper ? `<div class='${nodeWrap}'>${deeper}${childNodes}</div>` : '';
        return `<div class='${wrapper}'><div class='titel-header-bottom'><a href='/${esc(c.Slug)}'>${esc(c.Title)}</a></div>${content}</div>`;
      })
      .join('');
  };

  const liHtml = items
    .map((item) => {
      if (item.type === 'link') {
        const href = item.adress || '#';
        return `<li class=''><a target='${esc(item.target)}' class='grandparent' href='${esc(href)}'>${esc(item.text || href)}</a></li>`;
      }

      const category = item.categoryId ? categoryMap.get(item.categoryId) : undefined;
      const slug = item.slug || category?.Slug || '';
      const title = item.text || category?.Title || 'Kategorie';
      const href = `/${slug}`;
      let flyout = '';

      if (item.childnotes && category) {
        const nodes = renderNodes(category.nodes ?? []);
        const children = renderChildren(category.children ?? [], 2, Math.max(1, item.levels));
        if (nodes || children) {
          flyout = `<div class='fstlvl'>${nodes}${children}</div>`;
        }
      }

      return `<li class=''><a class='grandparent' href='${esc(href)}'>${esc(title)}</a>${flyout}</li>`;
    })
    .join('');

  return `<ul id='main-menu'>${liHtml}</ul>`;
}

export default function HeaderMenu(_: IHeaderMenuProps): React.ReactElement {
  const [showMenuPane, setShowMenuPane] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isContentMod, setIsContentMod] = useState(false);
  const [menuItems, setMenuItems] = useState<IEditableMenuItem[]>([]);
  const [categories, setCategories] = useState<ICategoryNode[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [draftItem, setDraftItem] = useState<IEditableMenuItem | null>(null);
  const [dropIndicator, setDropIndicator] = useState<{ index: number; position: DropPosition } | null>(null);
  const [showPreviewFlyin, setShowPreviewFlyin] = useState(false);
  const [previewSnapshotItems, setPreviewSnapshotItems] = useState<IEditableMenuItem[] | null>(null);
  const dragIndexRef = useRef<number | null>(null);

  const cloneMenuItems = (items: IEditableMenuItem[]): IEditableMenuItem[] => items.map((item) => ({ ...item }));

  const flatCategories = useMemo(() => flattenCategories(categories), [categories]);
  const categoryComboOptions: IComboBoxOption[] = flatCategories.map((c) => ({
    key: c.Id,
    text: `${decodeHtml(c.Title)} (${c.Slug})`,
  }));

  const categoryMap = useMemo(() => {
    const map = new Map<number, ICategoryNode>();
    flatCategories.forEach((c) => map.set(c.Id, c));
    return map;
  }, [flatCategories]);

  const sharedFieldStyles = {
    fieldGroup: {
      background: UI_COLORS.card,
      borderColor: UI_COLORS.border,
      borderWidth: 1,
      minHeight: 34,
      selectors: {
        '&:after': {
          borderColor: UI_COLORS.border,
        },
        ':hover': {
          borderColor: UI_COLORS.border,
          color: UI_COLORS.accentHover,
        },
        '&:hover:after': {
          borderColor: UI_COLORS.border,
        },
        ':focus-within': {
          borderColor: UI_COLORS.borderStrong,
          boxShadow: `0 0 0 1px ${UI_COLORS.borderStrong} inset`,
        },
        '&:focus-within:after': {
          borderColor: UI_COLORS.borderStrong,
        },
      },
    },
    field: {
      color: UI_COLORS.text,
      fontFamily: UI_FONT,
      selectors: {
        ':hover': {
          color: UI_COLORS.accentHover,
        },
      },
    },
    subComponentStyles: {
      label: {
        root: {
          color: UI_COLORS.text,
          fontFamily: UI_FONT,
        },
      },
    },
  };

  const sharedDropdownStyles = {
    dropdown: {
      ":focus::after":{
        border: "none !important"        
      },
      ":active .ms-Dropdown-caretDown":{
        color: UI_COLORS.accentHover
      },
      ":hover .ms-Dropdown-caretDown":{
        color: UI_COLORS.accentHover
      },
      ':hover .ms-Dropdown-title': {
          borderColor: UI_COLORS.border,
          color: UI_COLORS.accentHover
      },
      ":active .ms-Dropdown-title":{
          borderColor: UI_COLORS.accentHover,
          color: UI_COLORS.accentHover
      },
      "::after":{
          borderColor: "transparent !important",
          color:  "transparent !important"
      },
      dropdownOptionText:{
        ':hover': {
          borderColor: UI_COLORS.border,
          color: UI_COLORS.accentHover
        }
      },
      selectors: {
        ':hover': {
          borderColor: UI_COLORS.border,
          color: UI_COLORS.accentHover
        },
      },      
    },   
    title: {
      background: UI_COLORS.card,
      borderColor: UI_COLORS.border,
      color: `${UI_COLORS.text} !important`,
      fontFamily: UI_FONT,
      minHeight: 34,
      selectors: {
        '&:after': {
          borderColor: UI_COLORS.border,
        },
        ':hover': {
          borderColor: UI_COLORS.border,
          color: UI_COLORS.accentHover,
          background: UI_COLORS.card,
        },
        '&:hover:after': {
          borderColor: UI_COLORS.border,
        },
        ':focus': {
          borderColor: UI_COLORS.borderStrong,
          boxShadow: `0 0 0 1px ${UI_COLORS.borderStrong} inset`,
          color: UI_COLORS.accentHover,
        },
        '&:focus:after': {
          borderColor: UI_COLORS.borderStrong,
        },
      },
    },
    dropdownItem: {
      fontFamily: UI_FONT,
      color: UI_COLORS.text,
      selectors: {
        ':hover': {
          color: UI_COLORS.accentHover,
          background: UI_COLORS.accentBgHover,
        },
      },
    },
    dropdownItemSelected: {
      ":hover":{
      background: UI_COLORS.accent,
      color: UI_COLORS.accentBgHover,
      },
      background: UI_COLORS.accentBgHover,
      color: UI_COLORS.accent,
      fontFamily: UI_FONT,
    },    
    dropdownOptionText: {
      fontFamily: UI_FONT,
      color: UI_COLORS.text,
      selectors: {
        ':hover': {
          color: UI_COLORS.accentHover,
        },
      },
    },
    callout: {
      background: '#1a0f08',
      border: `1px solid ${UI_COLORS.border}`,
    },
    dropdownItemsWrapper: {
      background: '#1a0f08',
    },
    dropdownItems: {
      background: '#1a0f08',
    },
    label: {
      color: UI_COLORS.text,
      fontFamily: UI_FONT,
    },
    caretDown: {
      color: `${UI_COLORS.accent} !important`,
      fontSize: 14,
    },
    caretDownWrapper: {
      color: `${UI_COLORS.accent} !important`,
    },
  };

  const sharedComboBoxStyles = {
    container: {
      selectors: {
        '& .ms-Label': {
          color: `${UI_COLORS.text} !important`,
          fontFamily: UI_FONT,
        },
      },
      "& *:after": {
        border: "none !important"
      }
    },
    root: {
      background: UI_COLORS.card,
      border: `1px solid ${UI_COLORS.border} !important`,
      height: 39,
      selectors: {
        '&:after': {
          border: `1px solid ${UI_COLORS.border} !important`,
        },
      },
      "&:after": {
        border: "none !important"
      }
    },
    rootHovered: {
      background: UI_COLORS.card,
      border: `1px solid ${UI_COLORS.borderStrong} !important`,
      color: UI_COLORS.accentHover,
      /*selectors: {
        '&:hover:after': {
          border: `1px solid ${UI_COLORS.borderStrong} !important`,
        }
      },*/
      '&:hover .ms-ComboBox-Input':{
        color: UI_COLORS.text,
      }
    },
    rootFocused: {
      background: UI_COLORS.card,
      border: `1px solid ${UI_COLORS.borderStrong} !important`,
      //boxShadow: `0 0 0 1px ${UI_COLORS.borderStrong} inset`,
      selectors: {
        '&:focus:after': {
          border: `1px solid ${UI_COLORS.borderStrong} !important`,
        },
      },
    },
    input: {
      background: `${UI_COLORS.card} !important`,
      color: `${UI_COLORS.text} !important`,
      fontFamily: UI_FONT,
      selectors: {
        ':hover': {
          color: `${UI_COLORS.text} !important`,
        },
      },
    },
    callout: {
      background: '#1a0f08',
      border: `1px solid ${UI_COLORS.border}`,
    },
    optionsContainerWrapper: {
      background: '#1a0f08',
    },
    optionsContainer: {
      background: '#1a0f08',
    },
    caretDownButton:{
      root: {
        color: UI_COLORS.text,
        background: "rgba(0,0,0,0.4) !important",
        "&:hover": {
          color: UI_COLORS.borderStrong,
          background: "rgba(0,0,0,0.4)"
        },
        "&:active": {
          color: UI_COLORS.borderStrong,
          background: "rgba(0,0,0,0.4)"
        }
      },
      rootHovered: {
        color: UI_COLORS.borderStrong,
        background: "rgba(0,0,0,0.4)"
      },
      rootFocus: {
        color: UI_COLORS.borderStrong,
        background: "rgba(0,0,0,0.4)"
      }
    },
    comboBoxOptionStyles: {
      root: {
        color: UI_COLORS.text,        
        selectors: {
          '&:hover': { 
            background: UI_COLORS.text, 
            color: UI_COLORS.accentBgHover 
          },
        },
        "&.ms-Button--action":{
          color: UI_COLORS.text 
        },
         "&.ms-Button--action:hover":{
          color: UI_COLORS.accentBgHover,
          background: UI_COLORS.text, 
        },
         "&.ms-Button--action:active, &.ms-Button--action:focus":{
          color: UI_COLORS.accentBgHover,
          background: UI_COLORS.accent, 
        },
        "&.ms-Button--action.is-checked": {
          color: UI_COLORS.accentBgHover
        },
        "&.ms-Button--action.is-checked:hover": {
          color: UI_COLORS.dangerBgHover
        }
      },    
      optionText: { 
        fontFamily: UI_FONT,        
      }
    }
  };

  const sharedToggleStyles = {
      root: {
        marginTop: 2,
      },      
      label: {
        color: UI_COLORS.text,
        fontFamily: UI_FONT,
      },
      text: {
        color: UI_COLORS.text,
        fontFamily: UI_FONT,
      },
      pill: {
        background: UI_COLORS.card,
        borderColor: UI_COLORS.border,
        selectors: {
          ':hover': {
            borderColor: UI_COLORS.accent,
            background: UI_COLORS.card,
          },
          ':active': {
            borderColor: UI_COLORS.accent,
            background: UI_COLORS.card,
          },
          ':focus': {
            borderColor: UI_COLORS.accent,
            background: UI_COLORS.card,
          },
          ':focus:after': {
            borderColor: UI_COLORS.accent,
            background: UI_COLORS.card
          },
        },
      },
      thumb: {
        background: UI_COLORS.text + " !important",
        ':hover': {            
          background: UI_COLORS.accent,
        },
        ':active': {            
          background: UI_COLORS.accent,
        },
        ':focus': {            
          background: UI_COLORS.accent,
        },
        ':focus:after': {            
          background: UI_COLORS.accent
        },
      },
  };
  const sharedToggleCheckedStyles = {
    root: {
      marginTop: 2,
    },      
    label: {
      color: UI_COLORS.text,
      fontFamily: UI_FONT,
    },
    text: {
      color: UI_COLORS.text,
      fontFamily: UI_FONT,
    },
    pill: {
      background: UI_COLORS.card,
      borderColor: UI_COLORS.accent,
      selectors: {
        ':hover': {
          borderColor: UI_COLORS.accentBgHover,
          background: UI_COLORS.card,
        },
        ':active': {
          borderColor: UI_COLORS.accentBgHover,
          background: UI_COLORS.card,
        },
        ':focus': {
          borderColor: UI_COLORS.accentBgHover,
          background: UI_COLORS.card,
        },
        ':focus:after': {
          borderColor: UI_COLORS.accentBgHover,
          background: UI_COLORS.card
        },
      },
    },
    thumb: {
      background: UI_COLORS.accent + " !important",
      ':hover': {            
        background: UI_COLORS.accentBgHover,
      },
      ':active': {            
        background: UI_COLORS.accentBgHover,
      },
      ':focus': {            
        background: UI_COLORS.accentBgHover,
      },
      ':focus:after': {            
        background: UI_COLORS.accentBgHover
      },
    },
  };
  /*const ghostButtonStyles = {
    root: {
      background: UI_COLORS.card,
      borderColor: UI_COLORS.border,
      color: UI_COLORS.accent,
      fontFamily: UI_FONT,
    },
    rootHovered: {
      background: UI_COLORS.accentBgHover,
      borderColor: UI_COLORS.borderStrong,
      color: UI_COLORS.accentHover,
    },
  };*/

  /*const primaryButtonStyles = {
    root: {
      background: UI_COLORS.card,
      borderColor: UI_COLORS.border,
      color: UI_COLORS.accent,
      fontFamily: UI_FONT,
    },
    rootHovered: {
      background: UI_COLORS.accentBgHover,
      borderColor: UI_COLORS.borderStrong,
      color: UI_COLORS.accentHover,
    },
  };*/

  const loadPaneData = async () => {
    setIsLoading(true);
    setErrorMessage('');

    try {
      const [menuRes, roleRes, catsRes] = await Promise.all([
        fetch(`${API_URL}?resource=website-menu`, { credentials: 'include' }),
        fetch(`${API_URL}?resource=is-content-mod`, { credentials: 'include' }),
        fetch(`${API_URL}?resource=all`, { credentials: 'include' }),
      ]);

      const [menuJson, roleJson, catsJson] = await Promise.all([menuRes.json(), roleRes.json(), catsRes.json()]);

      const role = Boolean(roleJson?.isContentModerator);
      setIsContentMod(role);

      const parsedMenu: IEditableMenuItem[] = Array.isArray(menuJson?.data?.items)
        ? (menuJson.data.items as IApiMenuItem[]).map(mapApiItemToEditable)
        : [];

      setMenuItems(parsedMenu);
      setCategories(Array.isArray(catsJson?.data) ? (catsJson.data as ICategoryNode[]) : []);
      setIsLoaded(true);
    } catch {
      setErrorMessage('Die Menüdaten konnten nicht geladen werden.');
    } finally {
      setIsLoading(false);
    }
  };

  const openPane = async () => {
    setShowPreviewFlyin(false);
    setShowMenuPane(true);
    if (!isLoaded) {
      await loadPaneData();
    }
  };

  const closePane = () => {
    if (editIndex !== null || draftItem !== null) {
      return;
    }

    if (showPreviewFlyin && previewSnapshotItems) {
      setMenuItems(cloneMenuItems(previewSnapshotItems));
    }

    setShowMenuPane(false);
    setErrorMessage('');
  };

  const setItem = (idx: number, patch: Partial<IEditableMenuItem>) => {
    setMenuItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const addItem = () => {
    setMenuItems((prev) => [
      ...prev,
      {
        id: uid(),
        type: 'link',
        text: '',
        adress: '',
        target: '_self',
        showOnlyLoggedIn: false,
        slug: '',
        categoryId: null,
        childnotes: false,
        levels: 3,
      },
    ]);
  };

  const removeItem = (idx: number) => {
    setMenuItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const getDisplayTitle = (item: IEditableMenuItem): string => {
    if (item.type === 'link') {
      return item.text.trim() || item.adress.trim() || 'Link ohne Titel';
    }

    const category = item.categoryId ? categoryMap.get(item.categoryId) : undefined;
    return item.text.trim() || category?.Title || 'Kategorie ohne Titel';
  };

  const openEditDialog = (idx: number) => {
    setEditIndex(idx);
    setDraftItem({ ...menuItems[idx] });
  };

  const closeEditDialog = () => {
    setEditIndex(null);
    setDraftItem(null);
  };

  const applyEditDialog = () => {
    if (editIndex === null || !draftItem) {
      return;
    }

    setItem(editIndex, draftItem);
    closeEditDialog();
  };

  const serializeMenu = (): Array<Record<string, unknown>> => {
    return menuItems.map((item) => {
      if (item.type === 'link') {
        return {
          type: 'link',
          adress: item.adress,
          text: item.text,
          target: item.target,
          showOnlyLoggedIn: item.showOnlyLoggedIn,
        };
      }

      const selectedCategory = item.categoryId ? categoryMap.get(item.categoryId) : undefined;
      return {
        type: 'cat',
        slug: item.slug || selectedCategory?.Slug || '',
        id: item.categoryId ?? selectedCategory?.Id ?? '',
        text: item.text || selectedCategory?.Title || '',
        childnotes: item.childnotes,
        levels: item.levels,
      };
    });
  };

  const applyPreview = () => {
    const nav = document.getElementById('nav-main');
    if (!nav) {
      setErrorMessage('Nav-Container (#nav-main) wurde nicht gefunden.');
      return;
    }

    const html = buildPreviewHtml(menuItems, categoryMap);
    nav.innerHTML = html;

    setPreviewSnapshotItems(cloneMenuItems(menuItems));
    setShowMenuPane(false);
    setShowPreviewFlyin(true);
  };

  const saveMenu = async () => {
    setIsSaving(true);
    setErrorMessage('');

    try {
      const payload = {
        resource: 'save-website-menu',
        website_menu: serializeMenu(),
      };

      const res = await fetch(`${API_URL}?resource=save-website-menu`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const json = await res.json();
      if (!res.ok || json?.success !== true) {
        throw new Error(json?.error || 'Speichern fehlgeschlagen');
      }

      window.location.reload();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Speichern fehlgeschlagen';
      setErrorMessage(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const onDropAt = (dropIdx: number, position: DropPosition) => {
    const dragIdx = dragIndexRef.current;
    if (dragIdx === null) {
      setDropIndicator(null);
      return;
    }

    const insertAt = position === 'after' ? dropIdx + 1 : dropIdx;

    // No-op if item would be dropped at the same effective position.
    if (dragIdx === insertAt || dragIdx + 1 === insertAt) {
      setDropIndicator(null);
      dragIndexRef.current = null;
      return;
    }

    setMenuItems((prev) => {
      const next = [...prev];
      const [dragged] = next.splice(dragIdx, 1);
      const adjustedInsertAt = dragIdx < insertAt ? insertAt - 1 : insertAt;
      next.splice(adjustedInsertAt, 0, dragged);
      return next;
    });

    setDropIndicator(null);
    dragIndexRef.current = null;
  };

  return (
    <div>
      {showPreviewFlyin && (
        <div
          style={{
            position: 'fixed',
            right: 24,
            top: 200,
            zIndex: 2147483647,
            maxWidth: 420,
            background: UI_COLORS.accentBgHover,
            color: UI_COLORS.text,
            border: `1px solid ${UI_COLORS.accent}`,
            boxShadow: '0 10px 24px rgba(0,0,0,.45)',
            padding: '14px 16px',
            fontFamily: UI_FONT,
            animation: 'slidePreviewFlyin .25s ease-out',
          }}
          role="status"
          aria-live="polite"
        >
          <div style={{ fontWeight: 700, marginBottom: 4 }}>VORSCHAU AKTIV</div>
          <div>Es wurden noch keine Änderungen gespeichert. Bitte auf Speichern klicken, um die Menükonfiguration zu übernehmen.</div>
        </div>
      )}

      <style>{`@keyframes slidePreviewFlyin { from { opacity: 0; transform: translateY(-12px); } to { opacity: 1; transform: translateY(0); } }`}</style>

      <button
        type="button"
        onClick={openPane}
        style={{ width: '30px', height: '30px' }}
        className="mw-button mw-button--ghost mw-button--red mw-button--small mw-button--alert mw-button--edit post-edit-button"
      >
        <div className="mw-button-outer">
          <div className="mw-button-inner" />
        </div>
      </button>

      <Panel
        isOpen={showMenuPane}
        overlayProps={{isDarkThemed: true}}
        onDismiss={closePane}
        type={PanelType.large}
        isLightDismiss={false}
        headerText="Website-Menü konfigurieren"
        closeButtonAriaLabel="Schließen"
        styles={{
          main: { background: UI_PANEL_BG },
          content: { background: 'transparent', color: UI_COLORS.text, fontFamily: UI_FONT, height: '100%', display: 'flex', flexDirection: 'column', padding: '20px 0 0 0' },
          contentInner: { background: 'transparent', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', paddingBottom: 0 },
          scrollableContent: { background: 'transparent', height: '100%', display: 'flex', flexDirection: 'column' },
          headerText: { color: UI_COLORS.text, fontFamily: UI_FONT },
          commands: { background: 'transparent' },
          navigation: { background: 'transparent' },
          closeButton: {
            color: UI_COLORS.accent,
            selectors: {
              ':hover': {
                color: UI_COLORS.accentHover,
                background: UI_COLORS.accentBgHover,
              },
            },
          },
        }}
      >
        <div
          style={{
            position: 'relative',
            height: '100%',
            flex: 1,
            minHeight: 0,
            background: 'transparent',
            color: UI_COLORS.text,
            fontFamily: UI_FONT,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {(isLoading || isSaving) && (
            <Overlay isDarkThemed={true}>
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spinner styles={{
                  label: { color: UI_COLORS.text },
                  circle: { 
                    borderColor: `${UI_COLORS.accentBgHover} ${UI_COLORS.accent} ${UI_COLORS.text}`
                  }}} size={SpinnerSize.large} label={isSaving ? 'Speichern...' : 'Lade Menüdaten...'} />
              </div>
            </Overlay>
          )}

          {errorMessage && (
            <div style={{ marginBottom: 12, color: '#a4262c' }}>
              {errorMessage}
            </div>
          )}

          {!isLoading && !isContentMod && (
            <div style={{ marginBottom: 12, color: '#a4262c' }}>
              Du hast keine content_mod-Rechte. Das Menü ist nur lesbar.
            </div>
          )}

          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 24px' }}>
            <Stack tokens={{ childrenGap: DND_GAP_PX }}>
              {menuItems.map((item, idx) => (
                <div
                  key={item.id}
                  draggable
                  onDragStart={() => {
                    dragIndexRef.current = idx;
                  }}
                  onDragEnd={() => {
                    dragIndexRef.current = null;
                    setDropIndicator(null);
                  }}
                  onDragOver={(ev) => {
                    ev.preventDefault();
                    const rect = (ev.currentTarget as HTMLDivElement).getBoundingClientRect();
                    const position: DropPosition = ev.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
                    if (!dropIndicator || dropIndicator.index !== idx || dropIndicator.position !== position) {
                      setDropIndicator({ index: idx, position });
                    }
                  }}
                  onDrop={() => onDropAt(idx, dropIndicator?.position ?? 'before')}
                  style={{
                    border: `1px solid ${UI_COLORS.border}`,
                    borderRadius: 4,
                    padding: 10,
                    background: UI_COLORS.card,
                    position: 'relative',
                  }}
                >
                  {dropIndicator?.index === idx && dropIndicator.position === 'before' && (
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: -DND_LINE_OFFSET_PX,
                        height: DND_LINE_THICKNESS_PX,
                        background: '#fdf2b4',
                        pointerEvents: 'none',
                      }}
                    />
                  )}

                  {dropIndicator?.index === idx && dropIndicator.position === 'after' && (
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: -DND_LINE_OFFSET_PX,
                        height: DND_LINE_THICKNESS_PX,
                        background: '#fdf2b4',
                        pointerEvents: 'none',
                      }}
                    />
                  )}

                  <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                    <Label styles={{ root: { color: UI_COLORS.text, margin: 0, fontFamily: UI_FONT } }}>{getDisplayTitle(item)}</Label>
                    <Stack horizontal tokens={{ childrenGap: 2 }}>
                      <IconButton
                        iconProps={{ iconName: 'Edit' }}
                        title="Bearbeiten"
                        ariaLabel="Bearbeiten"
                        onClick={() => openEditDialog(idx)}
                        disabled={!isContentMod}
                        styles={{ root: { color: UI_COLORS.accent }, rootHovered: { color: UI_COLORS.accentHover, background: UI_COLORS.accentBgHover } }}
                      />
                      <IconButton
                        iconProps={{ iconName: 'Delete' }}
                        title="Entfernen"
                        ariaLabel="Entfernen"
                        onClick={() => removeItem(idx)}
                        disabled={!isContentMod}
                        styles={{ root: { color: UI_COLORS.accent }, rootHovered: { color: UI_COLORS.accentHover, background: UI_COLORS.dangerBgHover } }}
                      />
                    </Stack>
                  </Stack>
                </div>
              ))}
            </Stack>

            {!isLoading && !isSaving && (
              <div style={{ marginTop: 12 }}>
                <IconButton
                  iconProps={{ iconName: 'Add' }}
                  title="Eintrag hinzufügen"
                  ariaLabel="Eintrag hinzufügen"
                  onClick={addItem}
                  disabled={!isContentMod}
                  styles={{ root: { color: UI_COLORS.accent }, rootHovered: { color: UI_COLORS.accentHover, background: UI_COLORS.accentBgHover } }}
                />
              </div>
            )}
          </div>

          <div
            style={{
              marginTop: 'auto',
              flexShrink: 0,
              background: 'transparent',
              padding: '20px 24px',
              borderTop: `1px solid ${UI_COLORS.border}`,
              display: 'flex',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <button disabled={!isContentMod || isLoading || isSaving} onClick={saveMenu} className="mw-button mw-button--ghost mw-button--red mw-button--alert" id="submit">
                <div className="mw-button-outer">
                    <div className="mw-button-inner">Speichern</div>
                </div>
            </button>            
            <button disabled={isLoading || isSaving} onClick={applyPreview} type="reset" className="mw-button mw-button--ghost mw-button--yellow" id="reset">
                <div className="mw-button-outer">
                    <div className="mw-button-inner">Vorschau</div>
                </div>
            </button>
            <button disabled={isSaving} onClick={closePane} type="reset" className="mw-button mw-button--ghost mw-button--yellow" id="reset">
                <div className="mw-button-outer">
                    <div className="mw-button-inner">Abbrechen</div>
                </div>
            </button>
          </div>
        </div>
      </Panel>

      <Dialog
        hidden={editIndex === null || draftItem === null}
        onDismiss={closeEditDialog}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Menüeintrag bearbeiten',
          styles: {
            title: { color: UI_COLORS.text, fontFamily: UI_FONT },
            inner: { color: UI_COLORS.text, fontFamily: UI_FONT },
            subText: { color: UI_COLORS.text, fontFamily: UI_FONT },
          },
        }}
        modalProps={{
          styles: {
            main: { background: UI_COLORS.panel, color: UI_COLORS.text, fontFamily: UI_FONT, border: `1px solid ${UI_COLORS.accent}` },
            scrollableContent: { background: UI_COLORS.panel, color: UI_COLORS.text, fontFamily: UI_FONT, width: 400 },
          },
        }}
      >
        {draftItem && (
          <Stack tokens={{ childrenGap: 10 }}>
            <Dropdown
              label="Typ"
              selectedKey={draftItem.type}
              styles={sharedDropdownStyles}
              options={[
                { key: 'link', text: 'link' },
                { key: 'cat', text: 'cat' },
              ]}
              onChange={(_, option) => {
                const nextType = (option?.key as MenuType) || 'link';
                setDraftItem((prev) => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    type: nextType,
                  };
                });
              }}
            />

            {draftItem.type === 'link' ? (
              <>
                <TextField
                  label="Text"
                  value={draftItem.text}
                  styles={sharedFieldStyles}
                  onChange={(_, v) =>
                    setDraftItem((prev) =>
                      prev
                        ? {
                            ...prev,
                            text: v || '',
                          }
                        : prev,
                    )
                  }
                />
                <TextField
                  label="adress"
                  value={draftItem.adress}
                  styles={sharedFieldStyles}
                  onChange={(_, v) =>
                    setDraftItem((prev) =>
                      prev
                        ? {
                            ...prev,
                            adress: v || '',
                          }
                        : prev,
                    )
                  }
                />
                <Dropdown
                  label="target"
                  selectedKey={draftItem.target}
                  styles={sharedDropdownStyles}
                  options={[
                    { key: '_self', text: 'Eigene Seite' },
                    { key: '_blank', text: 'Neue Seite' },
                  ]}
                  onChange={(_, option) =>
                    setDraftItem((prev) =>
                      prev
                        ? {
                            ...prev,
                            target: (option?.key as '_self' | '_blank') || '_self',
                          }
                        : prev,
                    )
                  }
                />
                <Toggle
                  label="Nur für angemeldete Benutzer"
                  checked={draftItem.showOnlyLoggedIn}
                  styles={draftItem.showOnlyLoggedIn ? sharedToggleCheckedStyles : sharedToggleStyles}
                  onChange={(_, checked) =>
                    setDraftItem((prev) =>
                      prev
                        ? {
                            ...prev,
                            showOnlyLoggedIn: Boolean(checked),
                          }
                        : prev,
                    )
                  }
                />
              </>
            ) : (
              <>
                <ComboBox
                  label="Kategorie"
                  selectedKey={draftItem.categoryId ?? undefined}
                  options={categoryComboOptions}
                  styles={sharedComboBoxStyles}
                  caretDownButtonStyles={sharedComboBoxStyles.caretDownButton}
                  comboBoxOptionStyles={sharedComboBoxStyles.comboBoxOptionStyles}
                  allowFreeform
                  autoComplete="on"
                  useComboBoxAsMenuWidth                  
                  onChange={(_, option) => {
                    const categoryId = option ? Number(option.key) : null;
                    const cat = categoryId ? categoryMap.get(categoryId) : undefined;
                    setDraftItem((prev) =>
                      prev
                        ? {
                            ...prev,
                            categoryId,
                            slug: cat?.Slug || '',
                            text: prev.text || cat?.Title || '',
                          }
                        : prev,
                    );
                  }}
                />
                <TextField
                  label="Text"
                  value={draftItem.text}
                  styles={sharedFieldStyles}
                  onChange={(_, v) =>
                    setDraftItem((prev) =>
                      prev
                        ? {
                            ...prev,
                            text: v || '',
                          }
                        : prev,
                    )
                  }
                />
                <Toggle
                  label="Kinder anzeigen"
                  checked={draftItem.childnotes}
                  styles={draftItem.childnotes ? sharedToggleCheckedStyles : sharedToggleStyles}
                  onChange={(_, checked) =>
                    setDraftItem((prev) =>
                      prev
                        ? {
                            ...prev,
                            childnotes: Boolean(checked),
                          }
                        : prev,
                    )
                  }
                />
                <TextField
                  label="Maximale Ebenen"
                  type="number"
                  min={1}
                  max={8}
                  value={String(draftItem.levels)}
                  styles={sharedFieldStyles}
                  onChange={(_, v) => {
                    const parsed = Number(v || '3');
                    const levels = Number.isFinite(parsed) ? Math.max(1, Math.min(8, parsed)) : 3;
                    setDraftItem((prev) =>
                      prev
                        ? {
                            ...prev,
                            levels,
                          }
                        : prev,
                    );
                  }}
                />
              </>
            )}
          </Stack>
        )}
        <DialogFooter styles={{
          actions: {
            fontSize: "1em"
          },
          actionsRight: {
            fontSize: "1em"
          }
        }}>
          <button onClick={applyEditDialog} style={{ minWidth: 100 }} className="mw-button mw-button--ghost mw-button--red mw-button--alert" id="submit">
              <div className="mw-button-outer">
                <div className="mw-button-inner">Ok</div>
              </div>
          </button>            
          <button onClick={closeEditDialog} style={{ minWidth: 100 }} type="reset" className="mw-button mw-button--ghost mw-button--yellow" id="reset">
              <div className="mw-button-outer">
                <div className="mw-button-inner">Abbrechen</div>
              </div>
          </button>        
        </DialogFooter>
      </Dialog>
    </div>
  );
}
